/**
 * Persistent Video Player (YouTube-style shared element)
 *
 * Single <video>/<iframe> element mounted globally — never remounts during
 * fullscreen ↔ minimized transitions. Pure-HTML5 architecture (Option B):
 *
 *  - MediaSession API → lock screen play/pause/seek + Bluetooth controls
 *  - Picture-in-Picture API → app minimize → browser-native floating window
 *    keeps audio + video alive (Android Chromium WebView ≥ 70)
 *  - Touch drag (1 finger) → free-position the mini player anywhere
 *  - Pinch-zoom (2 fingers) → resize the mini player between 40vw..80vw
 *
 * State source of truth: videoMiniStore. Decoupled from the audio MiniPlayer
 * and the native alarm/audio service bridge.
 */

import React, { useRef, useEffect, CSSProperties, useState, useCallback } from 'react';
import { Play, Pause, X } from 'lucide-react';
import { videoMiniStore, useVideoMini } from '../../services/videoMiniStore';
import { API_URL } from '../../services/apiConfig';

const getYouTubeId = (url: string): string | null => {
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&?\s]{11})/
  );
  return match ? match[1] : null;
};

// Fullscreen portal header is ~40px tall + safe-area-inset-top.
// z-index 10000 is one above VideosView's portal (9999) so the actual
// <video> element renders OVER the placeholder div's bg-black box.
const FULLSCREEN_STYLE: CSSProperties = {
  position: 'fixed',
  top: 'calc(env(safe-area-inset-top, 0px) + 40px)',
  left: 0,
  right: 0,
  width: '100%',
  aspectRatio: '16 / 9',
  zIndex: 10000,
  backgroundColor: 'black',
  transition: 'top 280ms cubic-bezier(0.22, 1, 0.36, 1), right 280ms cubic-bezier(0.22, 1, 0.36, 1), bottom 280ms cubic-bezier(0.22, 1, 0.36, 1), width 280ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 280ms ease-out, box-shadow 280ms ease-out',
};

const MIN_VW = 40;
const MAX_VW = 80;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export const PersistentVideoPlayer: React.FC = () => {
  const { currentVideo, view, isPlaying, miniRect } = useVideoMini();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Touch gesture refs (don't trigger re-render)
  const gestureRef = useRef<{
    pointers: Map<number, { x: number; y: number }>;
    mode: 'idle' | 'drag' | 'pinch';
    dragStartRect: { bottom: number; right: number } | null;
    dragStartPointer: { x: number; y: number } | null;
    pinchStartDist: number;
    pinchStartVw: number;
    moved: boolean;
  }>({
    pointers: new Map(),
    mode: 'idle',
    dragStartRect: null,
    dragStartPointer: null,
    pinchStartDist: 0,
    pinchStartVw: 58,
    moved: false,
  });

  const [isPipActive, setIsPipActive] = useState(false);

  // Tracks the last time-position persisted to localStorage so we can
  // throttle writes to ~once every 3s of playback (avoid spamming).
  const lastPersistRef = useRef(0);

  // ───────────── Sync HTML5 element with store play/pause toggles ─────────────
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !currentVideo || currentVideo.sourceType === 'youtube') return;
    if (isPlaying && el.paused) {
      el.play().catch(() => { /* autoplay may reject — ignore */ });
    } else if (!isPlaying && !el.paused) {
      el.pause();
    }
  }, [isPlaying, currentVideo]);

  // ───────────── MediaSession API: lock screen + Bluetooth controls ─────────────
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentVideo) return;
    const ms = navigator.mediaSession;
    try {
      ms.metadata = new MediaMetadata({
        title: currentVideo.title || 'Video',
        artist: currentVideo.hindiTitle || 'Golok Divya Naam Kosh',
        album: 'Satsang Videos',
        artwork: currentVideo.thumbnailUrl
          ? [{
              src: currentVideo.thumbnailUrl.startsWith('http')
                ? currentVideo.thumbnailUrl
                : `${API_URL}${currentVideo.thumbnailUrl}`,
              sizes: '512x512',
              type: 'image/jpeg',
            }]
          : [],
      });
    } catch { /* metadata may throw on older WebViews */ }

    const tryPlay = () => {
      const el = videoRef.current;
      if (el) {
        el.play().then(() => videoMiniStore.setPlaying(true)).catch(() => {});
      } else {
        videoMiniStore.setPlaying(true);
      }
    };
    const tryPause = () => {
      const el = videoRef.current;
      if (el && !el.paused) el.pause();
      videoMiniStore.setPlaying(false);
    };
    const trySeek = (offset: number) => {
      const el = videoRef.current;
      if (!el) return;
      try { el.currentTime = clamp(el.currentTime + offset, 0, el.duration || el.currentTime + offset); } catch { /* */ }
    };

    try { ms.setActionHandler('play', tryPlay); } catch { /* */ }
    try { ms.setActionHandler('pause', tryPause); } catch { /* */ }
    try { ms.setActionHandler('seekbackward', () => trySeek(-10)); } catch { /* */ }
    try { ms.setActionHandler('seekforward', () => trySeek(10)); } catch { /* */ }
    try {
      ms.setActionHandler('seekto', (details: any) => {
        const el = videoRef.current;
        if (el && typeof details?.seekTime === 'number') {
          try { el.currentTime = details.seekTime; } catch { /* */ }
        }
      });
    } catch { /* */ }

    return () => {
      try { ms.setActionHandler('play', null); } catch { /* */ }
      try { ms.setActionHandler('pause', null); } catch { /* */ }
      try { ms.setActionHandler('seekbackward', null); } catch { /* */ }
      try { ms.setActionHandler('seekforward', null); } catch { /* */ }
      try { ms.setActionHandler('seekto', null); } catch { /* */ }
    };
  }, [currentVideo]);

  // Update playback state for lock screen UI
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    } catch { /* */ }
  }, [isPlaying]);

  // ───────────── Picture-in-Picture: enter on visibility change ─────────────
  // When the page becomes hidden (app minimize / tab switch), request PiP so
  // the browser keeps audio+video alive in a system floating window.
  useEffect(() => {
    if (!currentVideo || currentVideo.sourceType === 'youtube') return;
    const el = videoRef.current;
    if (!el) return;

    const onPipEnter = () => setIsPipActive(true);
    const onPipLeave = () => setIsPipActive(false);
    el.addEventListener('enterpictureinpicture', onPipEnter);
    el.addEventListener('leavepictureinpicture', onPipLeave);

    const onVisibility = async () => {
      if (document.hidden) {
        if (isPlaying && el.readyState >= 2 && !document.pictureInPictureElement &&
            (document as any).pictureInPictureEnabled !== false) {
          try {
            await (el as any).requestPictureInPicture();
          } catch { /* user denied / unsupported — fall back gracefully */ }
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      el.removeEventListener('enterpictureinpicture', onPipEnter);
      el.removeEventListener('leavepictureinpicture', onPipLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentVideo, isPlaying]);

  // ───────────── Touch gesture: drag + pinch on mini player ─────────────
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (view !== 'minimized') return;
    const g = gestureRef.current;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.moved = false;
    try { (e.target as Element).setPointerCapture?.(e.pointerId); } catch { /* */ }

    if (g.pointers.size === 1) {
      g.mode = 'drag';
      g.dragStartRect = { bottom: miniRect.bottom, right: miniRect.right };
      g.dragStartPointer = { x: e.clientX, y: e.clientY };
    } else if (g.pointers.size === 2) {
      const pts = Array.from(g.pointers.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      g.mode = 'pinch';
      g.pinchStartDist = Math.hypot(dx, dy) || 1;
      g.pinchStartVw = miniRect.widthVw;
    }
  }, [view, miniRect]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (view !== 'minimized') return;
    const g = gestureRef.current;
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (g.mode === 'drag' && g.dragStartRect && g.dragStartPointer && g.pointers.size === 1) {
      const dx = e.clientX - g.dragStartPointer.x;
      const dy = e.clientY - g.dragStartPointer.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) g.moved = true;
      const newRight = clamp(g.dragStartRect.right - dx, 0, window.innerWidth - 80);
      const newBottom = clamp(g.dragStartRect.bottom - dy, 0, window.innerHeight - 80);
      videoMiniStore.setMiniRect({ right: newRight, bottom: newBottom });
    } else if (g.mode === 'pinch' && g.pointers.size >= 2) {
      const pts = Array.from(g.pointers.values()).slice(0, 2);
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const ratio = dist / g.pinchStartDist;
      const newVw = clamp(g.pinchStartVw * ratio, MIN_VW, MAX_VW);
      g.moved = true;
      videoMiniStore.setMiniRect({ widthVw: newVw });
    }
  }, [view]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const g = gestureRef.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size < 2) {
      // exit pinch but keep dragging if 1 finger remains
      if (g.pointers.size === 1) {
        const [last] = Array.from(g.pointers.values());
        g.mode = 'drag';
        g.dragStartRect = { bottom: miniRect.bottom, right: miniRect.right };
        g.dragStartPointer = { x: last.x, y: last.y };
      } else {
        g.mode = 'idle';
        g.dragStartRect = null;
        g.dragStartPointer = null;
      }
    }
    // Suppress click after gesture
    if (g.moved) {
      e.stopPropagation();
      e.preventDefault();
      // give the click handler one tick to read 'moved' before we reset
      setTimeout(() => { g.moved = false; }, 50);
    }
  }, [miniRect]);

  if (!currentVideo || view === 'hidden') return null;

  const isYoutube = currentVideo.sourceType === 'youtube';
  const youtubeId = isYoutube ? getYouTubeId(currentVideo.sourceUrl) : null;
  const videoUrl =
    currentVideo.sourceType === 'upload'
      ? `${API_URL}${currentVideo.sourceUrl}`
      : currentVideo.sourceUrl;

  const isMini = view === 'minimized';

  // Build mini style dynamically from store rect (drag/resize live)
  const miniStyle: CSSProperties = isMini
    ? {
        position: 'fixed',
        top: 'auto',
        left: 'auto',
        bottom: `${miniRect.bottom}px`,
        right: `${miniRect.right}px`,
        width: `min(${miniRect.widthVw}vw, 380px)`,
        aspectRatio: '16 / 9',
        zIndex: 9998,
        backgroundColor: 'black',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 18px 40px rgba(0,0,0,0.38), 0 0 0 1px rgba(0,0,0,0.2)',
        cursor: 'grab',
        touchAction: 'none',
        transition: gestureRef.current.mode === 'idle'
          ? 'top 280ms cubic-bezier(0.22, 1, 0.36, 1), right 280ms cubic-bezier(0.22, 1, 0.36, 1), bottom 280ms cubic-bezier(0.22, 1, 0.36, 1), width 280ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 280ms ease-out, box-shadow 280ms ease-out'
          : 'none',
      }
    : FULLSCREEN_STYLE;

  // Hide the mini-window (DOM element stays mounted) while PiP is active so
  // we don't show a duplicate frame next to the system PiP.
  const containerStyle: CSSProperties = isPipActive && isMini
    ? { ...miniStyle, opacity: 0, pointerEvents: 'none' }
    : miniStyle;

  const handleExpand = () => {
    if (gestureRef.current.moved) return;
    if (!isMini) return;
    videoMiniStore.setView('fullscreen');
    videoMiniStore.resetMiniRect();
    window.history.pushState({ videoPlayer: 'fullscreen' }, '', window.location.pathname);
    // Notify App.tsx to switch to the Videos tab so the underlying page
    // matches the fullscreen video (avoids "video on top of random page").
    window.dispatchEvent(new CustomEvent('video-expand-navigate'));
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    try { videoRef.current?.pause(); } catch { /* */ }
    if (document.pictureInPictureElement) {
      try { (document as any).exitPictureInPicture?.(); } catch { /* */ }
    }
    videoMiniStore.close();
  };

  const handlePlayPauseMini = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isYoutube) return;
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  return (
    <div
      ref={containerRef}
      onClick={handleExpand}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={containerStyle}
      data-testid={isMini ? 'video-mini-player' : 'persistent-video-fullscreen'}
    >
      {isYoutube && youtubeId ? (
        <iframe
          title={currentVideo.title}
          className="w-full h-full"
          style={{ border: 0, pointerEvents: isMini ? 'none' : 'auto' }}
          src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&playsinline=1&rel=0`}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
        />
      ) : (
        <video
          ref={videoRef}
          className="w-full h-full object-contain bg-black"
          src={videoUrl}
          controls={!isMini}
          playsInline
          autoPlay
          preload="auto"
          onLoadedMetadata={(e) => {
            const el = e.currentTarget;
            const target = videoMiniStore.get().currentTime;
            if (target > 0 && Math.abs(el.currentTime - target) > 0.5) {
              try { el.currentTime = target; } catch { /* */ }
            }
          }}
          onTimeUpdate={(e) => {
            const el = e.currentTarget;
            videoMiniStore.setCurrentTime(el.currentTime);

            // Persist progress to localStorage every ~3s of playback so the
            // Home tab's "Continue Watching" carousel can show resumable
            // videos. Skips watches < 10s and >= 95% completed.
            try {
              const dur = isFinite(el.duration) ? el.duration : 0;
              const cur = el.currentTime;
              if (currentVideo && dur > 0 && cur > 10) {
                const last = lastPersistRef.current;
                if (Math.abs(cur - last) >= 3) {
                  lastPersistRef.current = cur;
                  const pct = (cur / dur) * 100;
                  const raw = localStorage.getItem('golok_video_progress');
                  const obj = raw ? JSON.parse(raw) : {};
                  if (pct >= 95) {
                    delete obj[currentVideo.id];
                  } else {
                    obj[currentVideo.id] = { time: cur, duration: dur };
                  }
                  localStorage.setItem('golok_video_progress', JSON.stringify(obj));
                }
              }
            } catch { /* persist best-effort */ }

            if ('mediaSession' in navigator && 'setPositionState' in navigator.mediaSession) {
              try {
                navigator.mediaSession.setPositionState({
                  duration: isFinite(el.duration) ? el.duration : 0,
                  position: el.currentTime,
                  playbackRate: el.playbackRate || 1,
                });
              } catch { /* */ }
            }
          }}
          onPlay={() => videoMiniStore.setPlaying(true)}
          onPause={(e) => {
            videoMiniStore.setPlaying(false);
            // Save progress immediately on pause (covers the "minimize" case
            // where onTimeUpdate may stop firing before our 3s threshold).
            try {
              const el = e.currentTarget;
              const dur = isFinite(el.duration) ? el.duration : 0;
              if (currentVideo && dur > 0 && el.currentTime > 10) {
                const pct = (el.currentTime / dur) * 100;
                const raw = localStorage.getItem('golok_video_progress');
                const obj = raw ? JSON.parse(raw) : {};
                if (pct >= 95) delete obj[currentVideo.id];
                else obj[currentVideo.id] = { time: el.currentTime, duration: dur };
                localStorage.setItem('golok_video_progress', JSON.stringify(obj));
              }
            } catch { /* persist best-effort */ }
          }}
        />
      )}

      {/* Mini-mode overlay controls */}
      {isMini && (
        <>
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-1 right-1 z-10 bg-black/75 hover:bg-black rounded-full p-1 backdrop-blur-sm"
            style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
            data-testid="video-mini-close-btn"
            aria-label="Close video"
          >
            <X size={14} className="text-white" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleExpand(); }}
            className="absolute top-1 left-1 z-10 bg-black/75 hover:bg-black rounded-full p-1 backdrop-blur-sm"
            style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
            data-testid="video-mini-expand-btn"
            aria-label="Expand video"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white">
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
            </svg>
          </button>
          {!isYoutube && (
            <button
              type="button"
              onClick={handlePlayPauseMini}
              className="absolute bottom-1 right-1 z-10 bg-black/75 hover:bg-black rounded-full p-1 backdrop-blur-sm"
              style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
              data-testid="video-mini-playpause-btn"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white" />}
            </button>
          )}
        </>
      )}
    </div>
  );
};
